import { Controller } from "@hotwired/stimulus"
import { FAMILY_LABELS } from "../weather_scenery"

// Orchestrates the Weather app: holds the current report, drives the hero scene
// (via the sibling weather-scene controller), refreshes realtime data on a slow
// poll, runs the city search, and powers Emulate mode — a manual preview of any
// condition, day or night, with a temperature slider. Realtime is the default;
// emulate never persists and never touches the network.
export default class extends Controller {
    static targets = [
        "scene", "temp", "unit", "condition", "city", "apparent", "humidity", "wind",
        "hourly", "daily", "emulatePanel", "emulateFlag", "modeButton",
        "searchInput", "searchResults", "searchPanel", "tempSlider", "tempReadout", "dayToggle",
        "unitToggle"
    ]

    static values = {
        forecastUrl: String,
        searchUrl: String,
        locationUrl: String,
        unitUrl: String,
        unit: { type: String, default: "fahrenheit" },
        report: Object,
        pollMs: { type: Number, default: 600000 }
    }

    connect() {
        this._emulating = false
        this._emuFamily = "clear"
        this._emuDay = true
        this._report = this.reportValue || {}
        this._unit = this._report?.units?.temperature || "°"
        this._searchSeq = 0
        this._poll = null

        this._scene = () => this.application.getControllerForElementAndIdentifier(this.sceneTarget, "weather-scene")

        this._applyReport(this._report)
        this._startPolling()
    }

    disconnect() {
        this._stopPolling()
        if (this._searchDebounce) clearTimeout(this._searchDebounce)
    }

    // --- Realtime polling ---
    _startPolling() {
        this._stopPolling()
        if (!this.hasForecastUrlValue) return
        this._poll = setInterval(() => {
            if (!this._emulating && !document.hidden) this.refresh()
        }, this.pollMsValue)
    }

    _stopPolling() {
        if (this._poll) clearInterval(this._poll)
        this._poll = null
    }

    async refresh() {
        try {
            const res = await fetch(this.forecastUrlValue, { headers: { Accept: "application/json" } })
            if (!res.ok) return
            const report = await res.json()
            this._report = report
            if (!this._emulating) this._applyReport(report)
        } catch (_e) { /* keep the last good scene */ }
    }

    // --- Apply a full report to the scene + readout + forecast rows ---
    _applyReport(report) {
        if (!report || !report.current) return
        this._unit = report.units?.temperature || this._unit
        const cur = report.current
        this._setScene(cur.scene?.family, cur.is_day, cur.wind)
        this._renderReadout(report.place, cur)
        this._renderHourly(report.hourly || [])
        this._renderDaily(report.daily || [])
        if (report.available === false) this._flagUnavailable()
    }

    _setScene(family, isDay, wind) {
        const scene = this._scene()
        if (scene && family) scene.apply({ family, isDay: !!isDay, wind: wind || 0 })
    }

    _renderReadout(place, cur) {
        if (this.hasTempTarget && cur.temp != null) this.tempTarget.textContent = `${cur.temp}`
        if (this.hasUnitTarget) this.unitTarget.textContent = this._unit
        if (this.hasConditionTarget) this.conditionTarget.textContent = cur.scene?.label || ""
        if (this.hasCityTarget && place) this.cityTarget.textContent = place.name || place.label || ""
        if (this.hasApparentTarget && cur.apparent != null) this.apparentTarget.textContent = `${cur.apparent}${this._unit}`
        if (this.hasHumidityTarget && cur.humidity != null) this.humidityTarget.textContent = `${cur.humidity}%`
        if (this.hasWindTarget && cur.wind != null) this.windTarget.textContent = `${cur.wind} ${this._report.units?.wind || "km/h"}`
    }

    _renderHourly(hours) {
        if (!this.hasHourlyTarget) return
        this.hourlyTarget.replaceChildren(...hours.map((h) => {
            const cell = document.createElement("div")
            cell.className = "wx-hour"
            cell.innerHTML =
                `<span class="wx-hour-time">${h.hour_label ?? ""}</span>` +
                `<i class="bi ${h.scene?.icon || "bi-cloud"} wx-hour-icon"></i>` +
                `<span class="wx-hour-temp">${h.temp != null ? h.temp + "°" : "–"}</span>` +
                `<span class="wx-hour-precip">${h.precip ? h.precip + "%" : ""}</span>`
            return cell
        }))
    }

    _renderDaily(days) {
        if (!this.hasDailyTarget) return
        this.dailyTarget.replaceChildren(...days.map((d) => {
            const row = document.createElement("div")
            row.className = "wx-day"
            row.innerHTML =
                `<span class="wx-day-label">${d.day_label ?? ""}</span>` +
                `<i class="bi ${d.scene?.icon || "bi-cloud"} wx-day-icon"></i>` +
                `<span class="wx-day-precip">${d.precip ? d.precip + "%" : ""}</span>` +
                `<span class="wx-day-range"><span class="wx-day-low">${d.low != null ? d.low + "°" : ""}</span>` +
                `<span class="wx-day-bar"></span>` +
                `<span class="wx-day-high">${d.high != null ? d.high + "°" : ""}</span></span>`
            return row
        }))
    }

    _flagUnavailable() {
        if (this.hasConditionTarget) this.conditionTarget.textContent = "Forecast unavailable"
    }

    // --- Emulate mode ---
    toggleEmulate(event) {
        if (event) event.preventDefault()
        this._emulating ? this.exitEmulate() : this.enterEmulate()
    }

    enterEmulate() {
        this._emulating = true
        this._stopPolling()
        this.element.classList.add("wx-emulating")
        if (this.hasEmulatePanelTarget) this.emulatePanelTarget.hidden = false
        if (this.hasEmulateFlagTarget) this.emulateFlagTarget.hidden = false
        if (this.hasModeButtonTarget) this.modeButtonTarget.classList.add("is-active")
        // Seed from the live condition.
        this._emuFamily = this._report?.current?.scene?.family || "clear"
        this._emuDay = this._report?.current?.is_day ?? true
        this._syncEmulateControls()
        this._applyEmulated()
    }

    exitEmulate() {
        this._emulating = false
        this.element.classList.remove("wx-emulating")
        if (this.hasEmulatePanelTarget) this.emulatePanelTarget.hidden = true
        if (this.hasEmulateFlagTarget) this.emulateFlagTarget.hidden = true
        if (this.hasModeButtonTarget) this.modeButtonTarget.classList.remove("is-active")
        this._applyReport(this._report)
        this._startPolling()
    }

    pickCondition(event) {
        const family = event.currentTarget.dataset.family
        if (!family) return
        this._emuFamily = family
        this._syncEmulateControls()
        this._applyEmulated()
    }

    toggleDayNight(event) {
        if (event) event.preventDefault()
        this._emuDay = !this._emuDay
        this._syncEmulateControls()
        this._applyEmulated()
    }

    slideTemp() {
        this._applyEmulated()
    }

    _syncEmulateControls() {
        if (this.hasEmulatePanelTarget) {
            this.emulatePanelTarget.querySelectorAll("[data-family]").forEach((btn) => {
                btn.classList.toggle("is-active", btn.dataset.family === this._emuFamily)
            })
        }
        if (this.hasDayToggleTarget) {
            this.dayToggleTarget.textContent = this._emuDay ? "Day" : "Night"
            this.dayToggleTarget.setAttribute("aria-pressed", String(!this._emuDay))
        }
    }

    _applyEmulated() {
        this._setScene(this._emuFamily, this._emuDay, 30)
        const temp = this.hasTempSliderTarget ? parseInt(this.tempSliderTarget.value, 10) : null
        if (this.hasTempReadoutTarget && temp != null) this.tempReadoutTarget.textContent = `${temp}${this._unit}`
        if (this.hasTempTarget && temp != null) this.tempTarget.textContent = `${temp}`
        if (this.hasConditionTarget) this.conditionTarget.textContent = FAMILY_LABELS[this._emuFamily] || ""
        if (this.hasCityTarget) this.cityTarget.textContent = "Emulated preview"
    }

    // --- Temperature unit ---
    setUnit(event) {
        const unit = event.currentTarget.dataset.unit
        if (!unit || unit === this.unitValue) return
        this._setUnit(unit)
    }

    async _setUnit(unit) {
        if (!this.hasUnitUrlValue) return
        const previous = this.unitValue
        this.unitValue = unit
        this._syncUnitToggle()
        try {
            const res = await fetch(this.unitUrlValue, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "X-CSRF-Token": this._csrf(),
                    Accept: "application/json"
                },
                body: new URLSearchParams({ unit })
            })
            if (!res.ok) throw new Error("unit update failed")
            const report = await res.json()
            this._report = report
            if (this._emulating) this.exitEmulate()
            this._applyReport(report)
        } catch (_e) {
            // Roll back on failure so the toggle doesn't lie about what's saved.
            this.unitValue = previous
            this._syncUnitToggle()
        }
    }

    _syncUnitToggle() {
        if (!this.hasUnitToggleTarget) return
        this.unitToggleTarget.querySelectorAll("[data-unit]").forEach((btn) => {
            btn.classList.toggle("is-active", btn.dataset.unit === this.unitValue)
        })
    }

    // --- City search ---
    onSearchInput() {
        if (this._searchDebounce) clearTimeout(this._searchDebounce)
        const q = this.searchInputTarget.value.trim()
        if (q.length < 2) { this._clearResults(); return }
        this._searchDebounce = setTimeout(() => this._runSearch(q), 260)
    }

    async _runSearch(q) {
        const seq = ++this._searchSeq
        try {
            const res = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } })
            if (!res.ok || seq !== this._searchSeq) return
            const { results } = await res.json()
            this._renderResults(results || [])
        } catch (_e) { /* ignore */ }
    }

    _renderResults(results) {
        if (!this.hasSearchResultsTarget) return
        this.searchResultsTarget.replaceChildren(...results.map((place) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = "wx-search-result"
            btn.textContent = place.label || place.name
            btn.addEventListener("click", () => this._choosePlace(place))
            return btn
        }))
        this.searchResultsTarget.hidden = results.length === 0
    }

    _clearResults() {
        if (this.hasSearchResultsTarget) {
            this.searchResultsTarget.replaceChildren()
            this.searchResultsTarget.hidden = true
        }
    }

    async _choosePlace(place) {
        this._clearResults()
        if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
        if (this.hasSearchPanelTarget) this.searchPanelTarget.hidden = true
        try {
            const body = new URLSearchParams({
                lat: place.lat, lon: place.lon, name: place.name || "",
                region: place.region || "", country: place.country || "",
                country_code: place.country_code || "", label: place.label || ""
            })
            const res = await fetch(this.locationUrlValue, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "X-CSRF-Token": this._csrf(),
                    Accept: "application/json"
                },
                body
            })
            if (!res.ok) return
            const report = await res.json()
            this._report = report
            if (this._emulating) this.exitEmulate()
            this._applyReport(report)
        } catch (_e) { /* ignore */ }
    }

    toggleSearch(event) {
        if (event) event.preventDefault()
        if (!this.hasSearchPanelTarget) return
        this.searchPanelTarget.hidden = !this.searchPanelTarget.hidden
        if (!this.searchPanelTarget.hidden && this.hasSearchInputTarget) this.searchInputTarget.focus()
    }

    _csrf() {
        return document.querySelector('meta[name="csrf-token"]')?.content || ""
    }
}
